import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum,
  InsertOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for insertOversightAssessment. The oversight_assessment
 * register is a near-1:1 twin of internal_audit_report (itself a twin of
 * assessment) and shares the InsertAssessmentInput wire shape, so the Status
 * field is the assessment status enum. Unlike internal_audit_report,
 * `Summary` is NOT NULL on oversight_assessment (schema.ts) and so is
 * required here.
 */
type InsertOversightAssessmentTRPCInput = {
  Title: string;
  Summary: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure so
 * consumers don't branch on which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertOversightAssessmentMutation => {
  return {
    insertOversightAssessmentApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertOversightAssessmentTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.oversight.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
        });
      },
    }),
  });

  return {
    insertOversightAssessment: async (
      variables: InsertOversightAssessmentTRPCInput
    ): Promise<InsertOversightAssessmentMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
